/**
 * Sign-up backdrop A/B test (TP-547), run as a PostHog feature-flag experiment.
 *
 * Flag key: `signup-blurred-backdrop` (multivariate: `control` / `test`)
 *   control → plain sign-up page (default, also the server-rendered state)
 *   test    → blurred product screenshot behind the sign-up form
 *
 * PostHog is loaded by Google Tag Manager, so `window.posthog` may not exist
 * yet and flags load asynchronously. Everything defaults to control until
 * PostHog says otherwise. Calling `getFeatureFlag` is what records the
 * `$feature_flag_called` exposure event that PostHog experiments use.
 *
 * GTM (and so PostHog) only loads in production, so on every other
 * environment `?signup-backdrop=test` (or `=control`) forces a variant for
 * design/QA. The override is ignored in production and skips PostHog, so it
 * never records an exposure.
 */

export const SIGNUP_BACKDROP_FLAG = 'signup-blurred-backdrop';
export const SIGNUP_BACKDROP_TEST = 'test';

const POLL_INTERVAL_MS = 500;
const MAX_ATTEMPTS = 41; // one immediate check, then every 500ms through 20s

const QUERY_PARAM = 'signup-backdrop';

// Undefined (e.g. unit tests) counts as production, so the override is opt-in.
const isProduction = () =>
  typeof __CLOUD_ENV__ === 'undefined' || /production/.test(__CLOUD_ENV__);

const getQueryOverride = () => {
  if (isProduction() || typeof window === 'undefined' || !window.location) {
    return null;
  }
  const match = new RegExp(`[?&]${QUERY_PARAM}=([^&#]*)`).exec(
    window.location.search || ''
  );
  const value = match && decodeURIComponent(match[1]);
  return value === SIGNUP_BACKDROP_TEST || value === 'control' ? value : null;
};

const getPostHog = () =>
  typeof window !== 'undefined' ? window.posthog : undefined;

const readVariant = (posthog) =>
  posthog.getFeatureFlag(SIGNUP_BACKDROP_FLAG) === SIGNUP_BACKDROP_TEST
    ? SIGNUP_BACKDROP_TEST
    : 'control';

/**
 * Calls `onVariant('control' | 'test')` once PostHog has flags for this
 * visitor, and again if they are reloaded. Returns an unsubscribe function.
 */
export const subscribeToSignupBackdropVariant = (onVariant) => {
  const override = getQueryOverride();
  if (override) {
    onVariant(override);
    return () => {};
  }

  let cancelled = false;
  let timer = null;
  let attempts = 0;

  const attach = () => {
    if (cancelled) return;
    const posthog = getPostHog();

    if (
      posthog &&
      typeof posthog.getFeatureFlag === 'function' &&
      typeof posthog.onFeatureFlags === 'function'
    ) {
      // Fires immediately if flags are already loaded, and on any reload.
      posthog.onFeatureFlags(() => {
        if (!cancelled) onVariant(readVariant(posthog));
      });
      return;
    }

    attempts += 1;
    if (attempts < MAX_ATTEMPTS) {
      timer = setTimeout(attach, POLL_INTERVAL_MS);
    }
  };

  attach();

  return () => {
    cancelled = true;
    if (timer) clearTimeout(timer);
  };
};
